import { z } from "zod/v3";

// ── Input Schema ──────────────────────────────────────────────────
export const GeoPipelineInputSchema = z.object({
  url: z.string().url(),
  targetQueries: z.array(z.string().min(1)).max(10).optional(),
  businessName: z.string().min(1),
  industry: z.string().optional(),
  businessDescription: z.string().min(1).max(2000).optional(),
});
export type GeoPipelineInput = z.infer<typeof GeoPipelineInputSchema>;

// ── Citation Result ───────────────────────────────────────────────
export const AiCitationResultSchema = z.object({
  engine: z.enum(["perplexity", "chatgpt", "gemini", "grok"]),
  query: z.string(),
  cited: z.boolean(),
  rank: z.number().optional(),
  citedUrl: z.string().optional(),
  topCompetitors: z.array(z.string()),
  snippet: z.string().optional(),
});
export type AiCitationResult = z.infer<typeof AiCitationResultSchema>;

// ── Schema Status ─────────────────────────────────────────────────
export const SchemaStatusSchema = z.object({
  status: z.enum(["MISSING", "PARTIAL", "COMPLETE"]),
  foundTypes: z.array(z.string()),
  missingTypes: z.array(z.string()),
  existingJsonLd: z.array(z.any()),
});
export type SchemaStatus = z.infer<typeof SchemaStatusSchema>;

// ── Site Scan ─────────────────────────────────────────────────────
export const SiteScanResultSchema = z.object({
  url: z.string(),
  title: z.string(),
  metaDescription: z.string().optional(),
  headingHierarchy: z.array(
    z.object({
      level: z.number(),
      text: z.string(),
    }),
  ),
  schema: SchemaStatusSchema,
  wordCount: z.number(),
  hasStructuredData: z.boolean(),
});
export type SiteScanResult = z.infer<typeof SiteScanResultSchema>;

// ── GEO Score ─────────────────────────────────────────────────────
export const GeoScoreSchema = z.object({
  overall: z.number().min(0).max(100),
  aiShareOfVoice: z.number().min(0).max(100),
  schemaScore: z.number().min(0).max(100),
  contentScore: z.number().min(0).max(100),
  entityScore: z.number().min(0).max(100),
});
export type GeoScore = z.infer<typeof GeoScoreSchema>;

// ── Content Gap ───────────────────────────────────────────────────
export const ContentGapSchema = z.object({
  section: z.string(),
  issue: z.string(),
  severity: z.enum(["critical", "warning", "info"]),
  recommendation: z.string(),
});
export type ContentGap = z.infer<typeof ContentGapSchema>;

// ── Copy Upgrade ──────────────────────────────────────────────────
export const CopyUpgradeSchema = z.object({
  section: z.string(),
  oldText: z.string(),
  newText: z.string(),
  informationGain: z.string(),
  approved: z.boolean().default(true),
});
export type CopyUpgrade = z.infer<typeof CopyUpgradeSchema>;

// ── JSON-LD Asset ─────────────────────────────────────────────────
export const JsonLdAssetSchema = z.object({
  type: z.string(),
  jsonLd: z.any(),
  approved: z.boolean().default(true),
});
export type JsonLdAsset = z.infer<typeof JsonLdAssetSchema>;

// ── Entity Payload ────────────────────────────────────────────────
export const EntityPayloadSchema = z.object({
  officialName: z.string(),
  shortBio: z.string(),
  missionStatement: z.string().optional(),
  yearFounded: z.string().optional(),
  coreFeatures: z.array(z.string()),
  dataPoints: z.array(z.string()),
  distributionTargets: z.array(
    z.object({
      platform: z.string(),
      fieldName: z.string(),
      content: z.string(),
    }),
  ),
});
export type EntityPayload = z.infer<typeof EntityPayloadSchema>;

// ── Implementation Plan ────────────────────────────────────────────
export const GeoImplementationTaskSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: z.enum(["crawl", "indexing", "schema", "content", "entity", "measurement"]),
  priority: z.enum(["P0", "P1", "P2"]),
  impact: z.enum(["high", "medium", "low"]),
  effort: z.enum(["low", "medium", "high"]),
  owner: z.enum(["engineering", "seo", "content", "ops"]),
  whyItMatters: z.string(),
  actions: z.array(z.string()).min(1).max(10),
  validation: z.array(z.string()).min(1).max(8),
  filePath: z.string().optional(),
  snippet: z.string().optional(),
  blockedBy: z.array(z.string()).default([]),
});
export type GeoImplementationTask = z.infer<typeof GeoImplementationTaskSchema>;

export const GeoImplementationArtifactSchema = z.object({
  robotsTxt: z.string(),
  llmsTxt: z.string(),
  sitemapActions: z.array(z.string()).min(1).max(10),
  metadataTemplate: z.string(),
  qaChecklist: z.array(z.string()).min(3).max(15),
});
export type GeoImplementationArtifact = z.infer<typeof GeoImplementationArtifactSchema>;

export const GeoCmsPlaybookSchema = z.object({
  platform: z.string(),
  steps: z.array(z.string()).min(2).max(12),
});
export type GeoCmsPlaybook = z.infer<typeof GeoCmsPlaybookSchema>;

export const GeoImplementationPackSchema = z.object({
  executiveSummary: z.string(),
  quickWins: z.array(z.string()).min(3).max(8),
  tasks: z.array(GeoImplementationTaskSchema).min(4).max(14),
  artifacts: GeoImplementationArtifactSchema,
  cmsPlaybooks: z.array(GeoCmsPlaybookSchema).min(2).max(8),
});
export type GeoImplementationPack = z.infer<typeof GeoImplementationPackSchema>;

// ── Pipeline Steps ────────────────────────────────────────────────
export const GEO_PIPELINE_STEPS = [
  { label: "Business Intake", icon: "settings", service: "User Input" },
  { label: "AI Citation Audit", icon: "search", service: "Perplexity + Gemini" },
  { label: "Gap Analysis", icon: "bar-chart", service: "Gemini Pro" },
  { label: "Asset Generation", icon: "code", service: "Gemini Pro" },
  { label: "Entity Authority", icon: "globe", service: "Gemini Pro" },
  { label: "Implementation Plan", icon: "zap", service: "Gemini Pro" },
];
